import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import { Package, TrendingUp, TrendingDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const InventoryPage = () => (
  <Layout>
    <InventoryContent />
  </Layout>
);

export const InventoryContent = () => {
  const [movements, setMovements] = useState([]);
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    product_id: '',
    type: 'entrada',
    quantity: '',
    reason: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadMovements();
    loadProducts();
  }, []);

  const loadMovements = async () => {
    try {
      const { data } = await axios.get(`${API}/inventory/movements`, { withCredentials: true });
      setMovements(data);
    } catch (error) {
      console.error('Erro ao carregar movimentações:', error);
    }
  };

  const loadProducts = async () => {
    try {
      const { data } = await axios.get(`${API}/products`, { withCredentials: true });
      setProducts(data);
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const product = products.find(p => p.id === formData.product_id);
      await axios.post(`${API}/inventory/movement`, {
        ...formData,
        product_name: product.name,
        quantity: parseFloat(formData.quantity)
      }, { withCredentials: true });

      loadMovements();
      loadProducts();
      setFormData({ product_id: '', type: 'entrada', quantity: '', reason: '', notes: '' });
      setShowForm(false);
    } catch (error) {
      console.error('Erro ao registrar movimentação:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8" data-testid="inventory-section">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-black text-slate-900 tracking-tight">Estoque</h2>
        <Button onClick={() => setShowForm(!showForm)} className="bg-blue-600 hover:bg-blue-700">
          <Package size={20} className="mr-2" />
          Nova Movimentação
        </Button>
      </div>

        {showForm && (
          <div className="bg-white rounded-md border border-slate-200 p-6 mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Nova Movimentação</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Produto *</Label>
                  <Select value={formData.product_id} onValueChange={(value) => setFormData({ ...formData, product_id: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um produto" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map((product) => (
                        <SelectItem key={product.id} value={product.id}>
                          {product.name} (Estoque: {product.stock_quantity} {product.unit})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Tipo *</Label>
                  <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="entrada">Entrada</SelectItem>
                      <SelectItem value="saida">Saída</SelectItem>
                      <SelectItem value="ajuste">Ajuste</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Quantidade *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Motivo *</Label>
                  <Input
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>Observações</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                  {loading ? 'Salvando...' : 'Salvar'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Data</th>
                  <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Produto</th>
                  <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Tipo</th>
                  <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Quantidade</th>
                  <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Motivo</th>
                  <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Usuário</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {movements.map((movement) => (
                  <tr key={movement.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {new Date(movement.created_at).toLocaleString('pt-BR')}
                    </td>
                    <td className="px-6 py-4 font-medium text-slate-900">{movement.product_name}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {movement.type === 'entrada' ? (
                          <TrendingUp size={18} className="text-green-600" />
                        ) : (
                          <TrendingDown size={18} className="text-red-600" />
                        )}
                        <span className="capitalize">{movement.type}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 font-medium">{movement.quantity}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{movement.reason}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{movement.user_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
    </div>
  );
};
